import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import completeImg from "../assets/complete.webp";
import stellImg from "../assets/stella.webp";
import amreshprajapatiImg from "../assets/amreshprajapati.png";
import logoImg from "../assets/logo.webp";

import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaInstagram,
  FaLinkedinIn,
  FaFacebookF,
  FaWhatsapp,
  FaCode,
  FaPalette,
  FaSearch,
  FaBox,
  FaCheckCircle,
  FaHtml5,
  FaCss3Alt,
  FaJs,
  FaReact,
  FaWordpress,
  FaBootstrap,
  FaArrowRight,
  FaStar,
  FaHome,
  FaChevronDown,
} from "react-icons/fa";
import { SiTailwindcss } from "react-icons/si";
import "./Home.css";

const homeServiceLinks = [
  { path: "/services/website-design", label: "Website Design" },
  { path: "/services/web-development", label: "Web Development" },
  { path: "/services/wordpress-development", label: "WordPress Development" },
  {
    path: "/services/ecommerce-website-development",
    label: "E-Commerce Solutions",
  },
  { path: "/services/seo-services", label: "SEO Services" },
  { path: "/services/packaging-design", label: "Packaging Design" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const skills = [
  { name: "HTML", pct: 92, icon: <FaHtml5 /> },
  { name: "CSS", pct: 85, icon: <FaCss3Alt /> },
  { name: "JavaScript", pct: 95, icon: <FaJs /> },
  { name: "React JS", pct: 90, icon: <FaReact /> },
  { name: "WordPress", pct: 95, icon: <FaWordpress /> },
  { name: "Bootstrap", pct: 80, icon: <FaBootstrap /> },
  { name: "Tailwind CSS", pct: 69, icon: <SiTailwindcss /> },
  { name: "Hosting Management", pct: 80, icon: <FaCode /> },
];

const services = [
  {
    icon: <FaPalette />,
    title: "Website Design",
    desc: "I create modern website UI designs using Figma, Canva and clean visual strategy.",
    count: "20+ Projects",
  },
  {
    icon: <FaCode />,
    title: "Development",
    desc: "I build fast, responsive and production-ready websites using HTML, CSS, React and WordPress.",
    count: "15+ Projects",
  },
  {
    icon: <FaSearch />,
    title: "SEO / Marketing",
    desc: "I optimize websites for better search visibility, speed, structure and user engagement.",
    count: "8+ Projects",
  },
  {
    icon: <FaBox />,
    title: "Creative Packaging Design",
    desc: "I create custom, eye-catching packaging concepts that help products stand out.",
    count: "25+ Projects",
  },
];

const stats = [
  { label: "Years of Experience", value: 4 },
  { label: "Projects Completed", value: 53 },
];

const reviews = [
  {
    name: "Pankaj Kumawat",
    text: "Great work, Amresh! The website interface you created is exactly what I had in mind. I really appreciate the new animation you added.",
    rating: 5,
  },
  {
    name: "Shree Krishna",
    text: "Good service. Had a great experience and got my website as expected and on time.",
    rating: 5,
  },
  {
    name: "Shruti Ghelani",
    text: "Professional and reliable developer. Delivered the project within the deadline with great quality.",
    rating: 5,
  },
];

const projects = [
  {
    num: "01",
    img: completeImg,
    cat: "React JS",
    title: "Complete Energy Group – Solar Solutions Website",
    desc: "A conversion-focused website for solar products, batteries and EV solutions, built with custom React JS code.",
  },
  {
    num: "02",
    img: stellImg,
    cat: "React JS",
    title: "Stella Designs – Creative Studio Website",
    desc: "A modern website for a 360° design studio focused on wall graphics, signage and placemaking, coded entirely in React JS.",
  },
];

/* ─── SEO: JSON-LD structured data for the homepage ──────────
   Built only from content that is actually rendered on this
   page (services, skills, projects, reviews) — nothing invented. */
const homeSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ProfessionalService",
      "@id": "https://amreshprajapati.com/#business",
      name: "Amresh Prajapati - Website Developer",
      url: "https://amreshprajapati.com/",
      telephone: "+919004068462",
      email: "info@amreshprajapati.com",
      image: "https://amreshprajapati.com/og-image.jpg",
      description:
        "Amresh Prajapati is a website developer and freelancer in Mumbai offering web design, React, WordPress and SEO services.",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Mumbai",
        addressLocality: "Mumbai",
        addressRegion: "Maharashtra",
        postalCode: "400066",
        addressCountry: "IN",
      },
      areaServed: ["Mumbai", "Virar", "Vasai", "Nallasopara", "Palghar"],
      geo: { "@type": "GeoCoordinates", latitude: 19.076, longitude: 72.8777 },
      openingHours: "Mo-Sa 09:00-20:00",
      priceRange: "₹₹",
      sameAs: [
        "https://www.instagram.com/amreshprajapatiwebdevloper/",
        "https://www.linkedin.com/in/amresh-prajapati/",
        "https://www.facebook.com/amreshprajapatiwebdevloper",
      ],
      makesOffer: services.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.title, description: s.desc },
      })),
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: (
          reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        ).toFixed(1),
        reviewCount: reviews.length,
      },
      review: reviews.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.name },
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
        },
        reviewBody: r.text,
      })),
    },
    {
      "@type": "Person",
      "@id": "https://amreshprajapati.com/#person",
      name: "Amresh Prajapati",
      jobTitle: "Website Developer & Freelancer",
      url: "https://amreshprajapati.com/",
      image: "https://amreshprajapati.com/og-image.jpg",
      email: "info@amreshprajapati.com",
      telephone: "+919004068462",
      worksFor: { "@id": "https://amreshprajapati.com/#business" },
      knowsAbout: skills.map((s) => s.name),
      sameAs: [
        "https://www.instagram.com/amreshprajapatiwebdevloper/",
        "https://www.linkedin.com/in/amresh-prajapati/",
        "https://www.facebook.com/amreshprajapatiwebdevloper",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://amreshprajapati.com/#website",
      url: "https://amreshprajapati.com/",
      name: "Amresh Prajapati",
      publisher: { "@id": "https://amreshprajapati.com/#person" },
      inLanguage: "en-IN",
    },
    {
      "@type": "WebPage",
      "@id": "https://amreshprajapati.com/#webpage",
      url: "https://amreshprajapati.com/",
      name: "Amresh Prajapati | Best Website Developer Freelancer in Mumbai",
      description:
        "Amresh Prajapati is a website developer and freelancer in Mumbai offering web design, React, WordPress and SEO services.",
      isPartOf: { "@id": "https://amreshprajapati.com/#website" },
      about: { "@id": "https://amreshprajapati.com/#business" },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://amreshprajapati.com/",
          },
        ],
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Who is Amresh Prajapati?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Amresh Prajapati is a professional website developer and freelancer based in Mumbai, India. He has 4+ years of experience and completed 53+ projects in web design, React JS, WordPress, SEO and digital marketing.",
          },
        },
        {
          "@type": "Question",
          name: "What services does Amresh Prajapati offer?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Amresh Prajapati offers website design, website development (React JS and WordPress), SEO and digital marketing, and creative packaging design services.",
          },
        },
        {
          "@type": "Question",
          name: "How can I hire a web developer in Mumbai?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Contact Amresh Prajapati on +91-9004068462, email info@amreshprajapati.com, or fill the contact form on amreshprajapati.com.",
          },
        },
        {
          "@type": "Question",
          name: "Does Amresh Prajapati build WordPress websites?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, Amresh Prajapati is an expert WordPress developer in Mumbai with 95% proficiency in WordPress development.",
          },
        },
      ],
    },
    {
      "@type": "ItemList",
      name: "Featured Projects by Amresh Prajapati",
      description: "Portfolio of web development projects completed by Amresh Prajapati",
      itemListElement: projects.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "CreativeWork",
          name: p.title,
          description: p.desc,
          creator: { "@id": "https://amreshprajapati.com/#person" },
        },
      })),
    },
  ],
};

export default function Home() {
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const statsRef = useRef(null);
  const [startCount, setStartCount] = useState(false);
  const [counts, setCounts] = useState(stats.map(() => 0));

  const handleHomeFormSubmit = (e) => {
    e.preventDefault();

    const form = e.currentTarget;

    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const phone = form.phone.value.trim();
    const service = form.service.value.trim();
    const message = form.message.value.trim();

    if (!name || !email || !phone || !service || !message) {
      alert("Please fill all required fields.");
      return;
    }

    const whatsappText = `Hello Amresh,

New enquiry from website:

Name / Company: ${name}
Email: ${email}
Phone: ${phone}
Service: ${service}
Message: ${message}`;

    alert("Thank you! Your form has been submitted successfully.");

    form.reset();

    window.open(
      `https://wa.me/919004068462?text=${encodeURIComponent(whatsappText)}`,
      "_blank"
    );
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartCount(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!startCount) return;

    stats.forEach((stat, index) => {
      let start = 0;
      const end = stat.value;
      const duration = 1500;
      const increment = end / (duration / 16);

      const counter = setInterval(() => {
        start += increment;

        if (start >= end) {
          start = end;
          clearInterval(counter);
        }

        setCounts((prev) => {
          const updated = [...prev];
          updated[index] = Math.floor(start);
          return updated;
        });
      }, 16);
    });
  }, [startCount]);

  return (
    <>
      <Helmet>
        <title>Amresh Prajapati | Best Website Developer Freelancer in Mumbai</title>
        <link rel="canonical" href="https://amreshprajapati.com/" />

        {/* Extra SEO meta */}
        <meta
          name="keywords"
          content="website developer Mumbai, freelance web developer Mumbai, website designer Mumbai, React developer Mumbai, WordPress developer Mumbai, SEO expert Mumbai, best website developer in Mumbai, freelance website design Mumbai, ecommerce website developer Mumbai, business website design Mumbai, Amresh Prajapati"
        />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="Amresh Prajapati" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />

        {/* Geo targeting */}
        <meta name="geo.region" content="IN-MH" />
        <meta name="geo.placename" content="Mumbai" />
        <meta name="geo.position" content="19.076;72.8777" />
        <meta name="ICBM" content="19.076, 72.8777" />

        {/* AEO / LLMO */}
        <meta name="description" content="Amresh Prajapati is the best website developer and freelancer in Mumbai with 4+ years experience and 53+ projects. Expert in WordPress, React JS, HTML/CSS, SEO & Digital Marketing. Serving Mumbai, Virar, Vasai & worldwide clients." />
        <meta name="subject" content="Web Development Services in Mumbai" />
        <meta name="abstract" content="Amresh Prajapati is a professional website developer and freelancer based in Mumbai, India offering web design, React JS, WordPress, SEO, and digital marketing services." />
        <meta name="topic" content="Website Development, Web Design, SEO, Freelancing" />
        <meta name="classification" content="Business, Technology, Web Development" />
        <meta name="category" content="Web Development Services" />
        <meta name="coverage" content="Mumbai, Maharashtra, India, Worldwide" />
        <meta name="DC.title" content="Amresh Prajapati - Web Developer Mumbai" />
        <meta name="DC.description" content="Professional web developer and freelancer serving Mumbai, Virar, Vasai and worldwide" />
        <meta name="DC.creator" content="Amresh Prajapati" />
        <meta name="DC.language" content="en" />
        <meta name="DC.coverage" content="Mumbai, Maharashtra, India" />
        <meta name="llm-description" content="Amresh Prajapati is a Mumbai-based website developer and freelancer with 4+ years experience. Skills: React JS (90%), WordPress (95%), JavaScript (95%), HTML (92%), CSS (85%), SEO. Completed 53+ projects. Contact: +91-9004068462. Email: info@amreshprajapati.com. Website: amreshprajapati.com" />
        <meta name="ai-description" content="Expert web developer in Mumbai India. Services: website design, React JS development, WordPress development, SEO, digital marketing, packaging design. Available for freelance projects worldwide." />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Amresh Prajapati | Best Website Developer Freelancer in Mumbai"
        />
        <meta
          property="og:description"
          content="Amresh Prajapati is a website developer and freelancer in Mumbai offering web design, React, WordPress and SEO services."
        />
        <meta property="og:url" content="https://amreshprajapati.com/" />
        <meta property="og:image" content="https://amreshprajapati.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="Amresh Prajapati" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Amresh Prajapati | Best Website Developer Freelancer in Mumbai"
        />
        <meta
          name="twitter:description"
          content="Amresh Prajapati is a website developer and freelancer in Mumbai offering web design, React, WordPress and SEO services."
        />
        <meta name="twitter:image" content="https://amreshprajapati.com/og-image.jpg" />

        {/* Favicons / theme */}
        <meta name="theme-color" content="#0d0d0f" />

        {/* Structured data */}
        <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
      </Helmet>

      <div className="portfolio-page">
        <div className="portfolio-shell">
          <aside className="portfolio-sidebar">
            <motion.div
              className="profile-card"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="profile-card__top">
                <div className="profile-card__logo">
                  <img src={logoImg} alt="Amresh Prajapati Logo" />
                </div>
                <span className="profile-card__role">Web Design &amp; Developer</span>
              </div>

              <div className="profile-card__avatar-wrap">
                <div className="profile-card__avatar">
                  <img
                    src={amreshprajapatiImg}
                    alt="Amresh Prajapati - Website Developer"
                  />
                </div>
                <div className="profile-card__ring" />
              </div>

              <div className="profile-card__info">
                <p>
                  <FaEnvelope className="profile-card__icon" />
                  info@amreshprajapati.com
                </p>
                <p>
                  <FaMapMarkerAlt className="profile-card__icon" />
                  Mumbai, Maharashtra
                </p>
                <p className="profile-card__copy">@2026 Amresh | All Rights Reserved</p>

                <a href="tel:+919004068462" className="profile-card__phone">
                  <FaPhone /> +91-9004068462
                </a>
              </div>

              <div className="profile-card__socials">
                <a
                  href="https://www.instagram.com/amreshprajapatiwebdevloper/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </a>
                <a
                  href="https://www.linkedin.com/in/amresh-prajapati/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn />
                </a>
                <a
                  href="https://www.facebook.com/amreshprajapatiwebdevloper"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FaFacebookF />
                </a>
                <a
                  href="https://api.whatsapp.com/send/?phone=919004068462"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp />
                </a>
              </div>

              <a href="#contact" className="btn btn--primary">
                <FaEnvelope /> Hire Me
              </a>
            </motion.div>
          </aside>

          <main className="portfolio-main">
            <div className={`home-header ${scrolled ? "scrolled" : ""}`}>
              <nav className="nav-links">
                <Link to="/" className={location.pathname === "/" ? "active" : ""}>
                  Home
                </Link>

                <Link to="/about" className={location.pathname === "/about" ? "active" : ""}>
                  About Us
                </Link>

                <div className="nav-item nav-item--dropdown">
                  <Link
                    to="/services"
                    className={location.pathname === "/services" ? "active" : ""}
                  >
                    Services
                    <FaChevronDown className="nav-caret" aria-hidden="true" />
                  </Link>

                  <div className="nav-dropdown">
                    {homeServiceLinks.map((svc) => (
                      <Link key={svc.path} to={svc.path}>
                        {svc.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link
                  to="/projects"
                  className={location.pathname === "/projects" ? "active" : ""}
                >
                  Projects
                </Link>

                <Link to="/blogs" className={location.pathname === "/blogs" ? "active" : ""}>
                  Blogs
                </Link>

                <Link
                  to="/contact"
                  className={location.pathname === "/contact" ? "active" : ""}
                >
                  Contact Us
                </Link>

                <Link to="/contact" className="hire-btn">
                  Hire Me
                </Link>
              </nav>
            </div>

            <section className="section section--first" id="home">
              <motion.div
                className="hero__intro"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <span className="section__tag section__tag--dark">
                  <FaHome /> INTRODUCE
                </span>

                <h1 className="hero__heading">
                  Hey There,
                  <br />
                  I&apos;m <span className="hero__accent">Amresh Prajapati,</span>
                  <br />A Passionate Website Developer Based In Mumbai, India
                </h1>

                <p className="hero__sub">
                  I design and build modern, responsive and business-focused websites
                  with clean UI, strong performance and premium visual impact.
                </p>

                <div className="hero__stats" ref={statsRef}>
                  {stats.map((s, i) => (
                    <div className="hero__stat stat-card" key={s.label}>
                      <span className="hero__stat-val">{counts[i]}+</span>
                      <span className="hero__stat-label">{s.label}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </section>

            <section className="section" id="about">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span className="section__tag section__tag--dark">ABOUT</span>
                <h2 className="section__heading">
                  Every great design begins with an even better story
                </h2>

                <p className="section__body">
                  Amresh Prajapati blends technical expertise with creative design to
                  build visually stunning and high-performing websites that leave a
                  lasting impression. As the{" "}
                  <strong>Best Website Developer in Mumbai</strong>, he seamlessly works
                  across both front-end and back-end development to bring digital ideas
                  to life.
                </p>

                <p className="section__body">
                  As a freelancer,{" "}
                  <span className="highlight-text">
                    I blend digital creativity with technical expertise
                  </span>
                  , ensuring every project stands out with a strong visual identity and
                  smooth user experience.
                </p>

                <p className="section__body">
                  Ready to elevate your online presence? Let&apos;s collaborate and create
                  something extraordinary together.
                </p>
              </motion.div>
            </section>

            <section className="section" id="services">
              <motion.div
                className="section__header section__header--left"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="section__tag section__tag--dark">SERVICES</span>
                <h2 className="section__heading">My Specializations</h2>
              </motion.div>

              <div className="services-grid">
                {services.map((svc, i) => (
                  <motion.div
                    key={svc.title}
                    className="service-card glass-card"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <div className="service-card__icon">{svc.icon}</div>
                    <div className="service-card__body">
                      <h3>{svc.title}</h3>
                      <p>{svc.desc}</p>
                      <span className="service-card__count">{svc.count}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="section" id="skills">
              <motion.div
                className="section__header section__header--left"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="section__tag section__tag--dark">MY SKILLS</span>
                <h2 className="section__heading">My Advantages</h2>
                <p className="section__sub section__sub--left">
                  More than 6+ technologies powering my website development work.
                </p>
              </motion.div>

              <div className="skills-grid">
                {skills.map((sk, i) => (
                  <motion.div
                    key={sk.name}
                    className="skill-card glass-card"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <div className="skill-card__icon">{sk.icon}</div>
                    <div className="skill-card__pct">{sk.pct}%</div>
                    <div className="skill-card__name">{sk.name}</div>
                    <div className="skill-card__bar">
                      <motion.div
                        className="skill-card__fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${sk.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="section" id="projects">
              <motion.div
                className="section__header section__header--left"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="section__tag section__tag--dark">PORTFOLIO</span>
                <h2 className="section__heading">Featured Projects</h2>
              </motion.div>

              <div className="featured-projects">
                {projects.map((p, i) => (
                  <motion.div
                    key={p.num}
                    className="featured-project glass-card"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="featured-project__badge">
                      <FaCheckCircle /> Project No. {p.num}
                    </div>

                    <div className="featured-project__img-wrap">
                      <img src={p.img} alt={p.title} loading="lazy" />
                      <div className="featured-project__overlay" />
                    </div>

                    <span className="featured-project__cat">{p.cat}</span>
                    <h3>{p.title}</h3>
                    <p>{p.desc}</p>
                  </motion.div>
                ))}
              </div>

              <div className="projects-cta">
                <Link to="/projects" className="btn btn--primary">
                  View More Projects <FaArrowRight />
                </Link>
              </div>
            </section>

            <section className="section" id="testimonials">
              <motion.div
                className="section__header section__header--left"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="section__tag section__tag--dark">GOOGLE REVIEWS</span>
                <h2 className="section__heading">Trusted by Clients</h2>
                <p className="section__sub section__sub--left">
                  Review cards styled for your dark website theme.
                </p>
              </motion.div>

              <div className="reviews-grid">
                {reviews.map((r, i) => (
                  <motion.div
                    key={r.name}
                    className="review-card glass-card"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <div className="review-card__stars">
                      {[...Array(r.rating)].map((_, j) => (
                        <FaStar key={j} />
                      ))}
                    </div>
                    <p className="review-card__text">{r.text}</p>
                    <div className="review-card__author">
                      <div className="review-card__avatar">{r.name[0]}</div>
                      <div>
                        <strong>{r.name}</strong>
                        <span>Google Review</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="google-review-actions">
                <a
                  href="https://share.google/He2FvGed2fRGZFrZv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--primary"
                >
                  See All Google Reviews
                </a>
              </div>
            </section>

            <section className="section" id="contact">
              <div className="contact-form-card glass-card glass-card--strong">
                <span className="section__tag section__tag--dark">CONTACT ME</span>
                <h2 className="contact-form__heading">Let&apos;s Work Together!</h2>

                <form className="contact-form-grid" onSubmit={handleHomeFormSubmit}>
                  <div className="contact-field contact-field--full">
                    <label>
                      Full Name / Company Name <span>*</span>
                    </label>
                    <input
                      name="name"
                      type="text"
                      placeholder="Full Name / Company Name"
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label>
                      Email <span>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="Email Address"
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label>
                      Your number <span>*</span>
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="Your number"
                      required
                    />
                  </div>

                  <div className="contact-field contact-field--full">
                    <label>
                      What Are You Looking For <span>*</span>
                    </label>
                    <select name="service" defaultValue="" required>
                      <option value="" disabled>
                        - Select a Service -
                      </option>
                      <option value="Website Development">Website Development</option>
                      <option value="Portfolio Website">Portfolio Website</option>
                      <option value="Landing Page">Landing Page</option>
                      <option value="React / Custom Development">React / Custom Development</option>
                      <option value="WordPress Website">WordPress Website</option>
                      <option value="SEO / Marketing / Branding">SEO / Marketing / Branding</option>
                      <option value="Domain / Hosting">Domain / Hosting</option>
                      <option value="Packaging Design">Packaging Design</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="contact-field contact-field--full">
                    <label>
                      Message <span>*</span>
                    </label>
                    <textarea
                      name="message"
                      rows="6"
                      placeholder="Message"
                      required
                    ></textarea>
                  </div>

                  <div className="contact-field contact-field--full">
                    <button type="submit" className="contact-submit-btn">
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </section>
          </main>
        </div>
      </div>
    </>
  );
}
